import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { configureApiClient } from '@/services/apiClient';
import { usersApi } from '@/services/endpoints';
import { queryKeys } from '@/services/queryClient';
import type { User } from '@/types/models';
import * as cognito from './cognito';
import { LOCAL_AUTH, getLocalIdentity, localHeaders, setLocalIdentity, type LocalIdentity } from './localAuth';

type Status = 'loading' | 'authenticated' | 'anonymous';

interface AuthContextValue {
  status: Status;
  user: User | undefined;
  /** UI hint only. The server re-checks the ADMIN group on every admin request. */
  isAdmin: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  /** Local development only; a no-op unless VITE_LOCAL_AUTH is set in a dev build. */
  signInLocal: (identity: LocalIdentity) => void;
  signOut: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [signedIn, setSignedIn] = useState<boolean | undefined>(undefined);

  const signOut = useCallback(() => {
    cognito.signOut();
    setLocalIdentity(null);
    setSignedIn(false);
    queryClient.clear();
  }, [queryClient]);

  // Wire the API client once: it fetches a fresh token per request (the Cognito SDK
  // refreshes it when needed) and signs the user out on any auth failure.
  useEffect(() => {
    configureApiClient({
      getAccessToken: LOCAL_AUTH ? async () => null : cognito.getAccessToken,
      onAuthFailure: signOut,
      ...(LOCAL_AUTH ? { getExtraHeaders: localHeaders } : {}),
    });
    if (LOCAL_AUTH) {
      setSignedIn(getLocalIdentity() !== null);
    } else {
      void cognito.getSession().then((s) => setSignedIn(s !== null));
    }
  }, [signOut]);

  const profile = useQuery({
    queryKey: queryKeys.currentUser(),
    queryFn: usersApi.me,
    enabled: signedIn === true,
    staleTime: 5 * 60_000,
  });

  const signIn = useCallback(async (email: string, password: string) => {
    await cognito.signIn(email, password);
    await queryClient.invalidateQueries({ queryKey: queryKeys.currentUser() });
    setSignedIn(true);
  }, [queryClient]);

  const signInLocal = useCallback((identity: LocalIdentity) => {
    setLocalIdentity(identity);
    void queryClient.invalidateQueries({ queryKey: queryKeys.currentUser() });
    setSignedIn(true);
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(() => ({
    status: signedIn === undefined ? 'loading' : signedIn ? 'authenticated' : 'anonymous',
    user: profile.data,
    isAdmin: profile.data?.admin === true,
    signIn,
    signInLocal,
    signOut,
  }), [signedIn, profile.data, signIn, signInLocal, signOut]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
